// The subscription: whether this phone may be interrupted, and the wall.
//
// See PAYWALL.md. The board, the lines, a trip and everything else stay free;
// what is sold is the sentence the app was built on — you should not have to
// open it. So there are exactly three gates (Alerts ON, Follow, the test
// alert) and every one of them runs through `requireEntitlement` below.
//
// ONE NATIVE PLUGIN, `Billing`, TWO IMPLEMENTATIONS. StoreKit 2 on iOS
// (`ios/App/App/BillingPlugin.swift`) and Play Billing on Android
// (`android/.../billing/BillingPlugin.kt`). Both answer the same six calls and
// hand back the same purchase proof, which is what the server verifies:
//
//   { store: 'apple', jws }                                   iOS
//   { store: 'google', productId, basePlanId, purchaseToken } Android
//
// GATED ONLY WHERE THE PLUGIN EXISTS. This file ships over the air, so it lands
// on installed builds whose native half has no Billing plugin at all (iOS build
// 37 and older, and the web). There is no way to pay on those, and a wall with
// nothing behind it is a feature taken away, so `available` stays false and
// every gate opens: Alerts and Follow behave exactly as they did.
//
// THE SERVER IS THE LAST WORD WHEN IT CAN BE ASKED. `/api/entitlement` checks
// the proof with Apple or Google; its answer wins. Offline, the store's own
// local state (StoreKit's verified current entitlements, Play's purchased
// state) stands in, because an alerts app that goes dark in a tunnel because it
// could not re-check a subscription is broken twice over.
//
// Everything that decides is in `createBilling`, which takes its plugin, its
// fetch, its storage and its clock as arguments. The module-level instance at
// the bottom wires the real ones; the suite wires fakes.

import { CITIES } from './city.js';
import { modesOf } from './modes.js';
import { computed, ref, shallowRef } from 'vue';
import { API_BASE, isNative, platform as nativePlatform, plugin as nativePlugin } from './core/native.js';
import { prefs } from './core/storage.js';
import { attribution } from './attribution.js';
import { isDemo } from './core/demo.js';

// ── The products, as the contract names them ─────────────────────────────────

export const APPLE_ANNUAL = 'com.harfredlabs.lateagain.annual';
export const APPLE_MONTHLY = 'com.harfredlabs.lateagain.monthly';
export const GOOGLE_PRODUCT = 'tellme';
export const GOOGLE_ANNUAL = 'annual';
export const GOOGLE_MONTHLY = 'monthly';

/** Where the Terms of Use link goes. Apple's standard EULA on iOS (3.1.2). */
export const APPLE_EULA = 'https://www.apple.com/legal/internet-services/itunes/dev/stdeula/';
export const TERMS_PATH = '/terms';
export const PRIVACY_PATH = '/privacy';
export const PLAY_MANAGE = `https://play.google.com/store/account/subscriptions?sku=${GOOGLE_PRODUCT}&package=com.harfredlabs.lateagain`;

const ENTITLEMENT_KEY = 'lateagain:entitlement:v1';
const LAPSE_KEY = 'lateagain:lapse-pending';
/** Set the first time this phone is entitled, and never cleared. */
const EVER_KEY = 'lateagain:ever-entitled';
/** When a background 402 last opened the wall on its own (ms), so it is not every sync. */
const REFUSED_WALL_KEY = 'lateagain:refused-wall-at';
/** A server refusal re-offers the wall on its own at most this often. */
export const REFUSED_WALL_EVERY_MS = 7 * 24 * 60 * 60 * 1000;
/** `{ usedAt, key }` once the one free Follow has been started. */
const FREE_FOLLOW_KEY = 'lateagain:free-follow';

/** How long a gate waits for the first answer before deciding without it. */
export const READY_WAIT_MS = 4000;

// ── Pure pieces ──────────────────────────────────────────────────────────────

/**
 * A purchase proof the server will accept, or null. Anything else — a missing
 * JWS, a Google proof with no token, a store nobody named — is not sent: the
 * server would refuse it and the refusal would read as "not entitled".
 */
export function validProof(proof) {
  if (!proof || typeof proof !== 'object') return null;
  if (proof.store === 'apple') {
    const jws = String(proof.jws ?? '');
    return jws.split('.').length === 3 ? { store: 'apple', jws } : null;
  }
  if (proof.store === 'google') {
    const purchaseToken = String(proof.purchaseToken ?? '');
    if (!purchaseToken) return null;
    return {
      store: 'google',
      productId: String(proof.productId || GOOGLE_PRODUCT),
      basePlanId: proof.basePlanId ? String(proof.basePlanId) : null,
      purchaseToken,
    };
  }
  return null;
}

/**
 * The plans, yearly first and monthly second, from whatever the plugin said.
 *
 * Only these two, in this order, whatever order the store answered in: the
 * wall offers yearly first (PAYWALL.md) and a product the store returns that
 * is not one of ours is not something this app sells.
 */
export function orderPlans(list) {
  const rows = (Array.isArray(list) ? list : []).filter((p) => p && p.price);
  const period = (p) => {
    const raw = String(p.period ?? '').toLowerCase();
    if (raw.startsWith('y') || raw === 'p1y' || p.id === APPLE_ANNUAL || p.basePlanId === GOOGLE_ANNUAL) return 'year';
    if (raw.startsWith('m') || raw === 'p1m' || p.id === APPLE_MONTHLY || p.basePlanId === GOOGLE_MONTHLY) return 'month';
    return null;
  };
  const out = [];
  for (const want of ['year', 'month']) {
    const found = rows.find((p) => period(p) === want);
    if (!found) continue;
    const trialDays = Number(found.trialDays ?? found.trial?.days ?? 0);
    out.push({
      id: String(found.id ?? found.basePlanId ?? want),
      productId: found.productId ?? found.id ?? null,
      basePlanId: found.basePlanId ?? null,
      period: want,
      price: String(found.price),
      trialDays: Number.isFinite(trialDays) && trialDays > 0 ? trialDays : 0,
      // Eligible means the store says THIS account can have the trial. Apple
      // asks per Apple ID; Play only returns a trial offer to somebody who can
      // take it. Anything unsure is not a trial: naming a free week somebody
      // then pays for is the one wrong thing this screen could say.
      trialEligible: found.trialEligible === true && trialDays > 0,
    });
  }
  return out;
}

/** "A$24.99 a year". */
export function priceLine(plan) {
  return `${plan.price} a ${plan.period}`;
}

/** "A$24.99/year". */
export function priceShort(plan) {
  return `${plan.price}/${plan.period}`;
}

/** "7 days" / "1 day" / "14 days". */
function span(days) {
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

/** "7 days free, then A$24.99 a year", or null with no trial on offer. */
export function trialLine(plan) {
  if (!plan?.trialEligible || !plan.trialDays) return null;
  return `Free trial: ${span(plan.trialDays)} free, then ${priceLine(plan)}`;
}

/**
 * A price the store formatted, read back into a number and the currency around
 * it: "A$24.99" → { amount: 24.99, before: 'A$', after: '', decimals: 2 }.
 *
 * THE PLUGINS HAND OVER ONLY THE DISPLAY STRING (StoreKit's `displayPrice`,
 * Play's `formattedPrice`), and this ships over the air to builds whose native
 * half cannot be changed, so the number is recovered from the string. Anything
 * this cannot read with confidence is null, and a null hides the per-month
 * line rather than printing a wrong one: a sum about somebody's money that is
 * off by a decimal point is worse than no sum.
 */
export function parsePrice(text) {
  const raw = String(text ?? '').replace(/[\u00a0\u202f]/g, ' ').trim();
  const m = raw.match(/^(\D*)(\d[\d.,' ]*\d|\d)(\D*)$/);
  if (!m) return null;
  const [, before, digits, after] = m;
  if (!before.trim() && !after.trim()) return null;
  let number = digits.replace(/[' ]/g, '');
  let decimals = 0;
  const lastSep = Math.max(number.lastIndexOf('.'), number.lastIndexOf(','));
  if (lastSep !== -1 && number.length - lastSep - 1 === 2) {
    // A separator followed by exactly two digits is the decimal point, in
    // either convention ("24.99", "24,99", "1.234,56", "1,234.56").
    decimals = 2;
    number = `${number.slice(0, lastSep).replace(/[.,]/g, '')}.${number.slice(lastSep + 1)}`;
  } else {
    // "1,500" yen, "2.500" — grouping only, a whole number.
    number = number.replace(/[.,]/g, '');
  }
  const amount = Number(number);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const decimalMark = decimals && digits.replace(/[' ]/g, '')[lastSep] === ',' ? ',' : '.';
  return { amount, before, after, decimals, decimalMark };
}

/** A parsed price with a new amount, in the same currency and style. */
function formatLike(p, amount) {
  const fixed = amount.toFixed(Math.max(2, p.decimals));
  const body = p.decimalMark === ',' ? fixed.replace('.', ',') : fixed;
  return `${p.before}${body}${p.after}`;
}

/**
 * What yearly works out to against monthly, from the store's own prices:
 * `{ perMonth: 'A$2.08', percent: 30, line: 'A$2.08 a month · 30% less than
 * monthly' }`, or null.
 *
 * NULL WHEREVER IT COULD MISLEAD: either price missing or unreadable, the two
 * in different currencies (or formatted differently, which is the same doubt),
 * or no saving at all. The percentage is rounded DOWN so it never claims more
 * than the store's prices give. Apple (3.1.2) and Play both require the billed
 * amount to be the most prominent price; this is the subordinate line under it.
 */
export function planSaving(plans) {
  const year = (plans ?? []).find((p) => p?.period === 'year');
  const month = (plans ?? []).find((p) => p?.period === 'month');
  if (!year || !month) return null;
  const y = parsePrice(year.price);
  const mo = parsePrice(month.price);
  if (!y || !mo) return null;
  if (y.before.trim() !== mo.before.trim() || y.after.trim() !== mo.after.trim()) return null;
  const percent = Math.floor((1 - y.amount / (mo.amount * 12)) * 100);
  if (!(percent > 0)) return null;
  const perMonth = formatLike(y, Math.round((y.amount / 12) * 100) / 100);
  return { perMonth, percent, line: `${perMonth} a month · ${percent}% less than monthly` };
}

/** The line the demo is drawn in, when nobody is watching one yet. */
export const DEMO_FALLBACK_LINE = Object.freeze({ name: 'Pakenham', colour: '#279fd5' });

/**
 * The two lock-screen surfaces the wall shows, as data: a disruption alert and
 * a followed train. Drawn by `PaywallDemo.vue` in pure CSS, in the colour of
 * the first line this person watches — their line, not a stock photo of
 * somebody else's.
 *
 * ORDER FOLLOWS THE ASK. Somebody who pressed Follow sees the followed train
 * first; everybody else sees the alert first, because that is what Alerts ON
 * and a test alert are for.
 */
export function paywallDemo({ reason = 'alerts', line = null, platform = 'ios' } = {}) {
  const name = String(line?.name || DEMO_FALLBACK_LINE.name);
  const colour = /^#[0-9a-f]{3,8}$/i.test(String(line?.colour ?? '')) ? line.colour : DEMO_FALLBACK_LINE.colour;
  return {
    order: reason === 'follow' ? ['follow', 'alert'] : ['alert', 'follow'],
    colour,
    alert: {
      stamp: 'LATE AGAIN · NOW',
      title: `${name} line: major delays.`,
      body: 'Signal fault at Caulfield. Allow 30 minutes.',
    },
    // THE LIVE ACTIVITY, as it is drawn on the lock screen (FollowBanner): the
    // line and where it is heading, how late, the countdown to the stop, the
    // rail with a letter under each station, and what is next. A trip between
    // stations most lines share, so it is true whichever line is the person's.
    follow: {
      head: `${name.toUpperCase()} · TO THE CITY`,
      late: '+3',
      clock: '14:20',
      until: 'UNTIL YOUR STOP',
      stops: ['CAR', 'CAU', 'MAL', 'ANZ', 'TH', 'SL'],
      at: 0.14,
      next: 'Caulfield',
      nextIn: '0:44',
      where: platform === 'android' ? 'live notification' : 'Dynamic Island',
    },
  };
}

/**
 * The three things the subscription is, one line each. Every line is true on
 * every phone that can see the wall, or says only what is true of it:
 *
 *   follow  Follow works for a train in every city and for trams and buses where
 *           the mode is shown (src/mode-follow.js), so the noun is the ride's
 *           own when the caller knows it (`noun`) and "train" otherwise. The
 *           surface is the Dynamic Island on an iPhone and a live notification on
 *           Android (see `paywallDemo`'s old `where`).
 *   alarm   The station alarm (src/station-alarm.js, `stationAlarmAt`) rings as
 *           the stop comes up: ninety seconds out, or as the vehicle leaves the
 *           call before yours when the feed names it. Not "one stop before" for
 *           every mode, so it does not say that.
 *   alerts  Alerts go for the lines this phone watches (`/api/push/subscribe`
 *           carries lines) and respect quiet hours (`inAnyQuietWindow`).
 *
 * `icon` names the glyph the sheet draws.
 */
export function paywallBenefits({ platform = 'ios', freeFollow = false, noun = 'train', city = null } = {}) {
  const facts = CITIES[city] ?? null;
  // A CITY WITH NOTHING LIVE HAS NOTHING TO FOLLOW OR ALERT ON (Perth: only a
  // timetable; `canFollow` says 'timetable'). Say that, promise nothing.
  if (facts?.live === false) {
    return [{
      key: 'TIMETABLE', icon: 'alerts', note: null,
      text: `${facts.name} publishes only a timetable, so there is nothing live to follow or alert on yet.`,
    }];
  }
  const surface = platform === 'android'
    ? 'on the lock screen and in a live notification'
    : 'on the lock screen and Dynamic Island';
  const rows = [
    { key: 'FOLLOW', icon: 'follow', text: `Your ${noun} ${surface}.`, note: freeFollow ? 'Your first Follow is free.' : null },
    { key: 'ALARM', icon: 'alarm', text: 'A station alarm as your stop comes up.' },
  ];
  if (facts?.hasAlerts !== false) rows.push({ key: 'ALERTS', icon: 'alerts', text: 'Alerts for your lines only, with quiet hours.' });
  return rows;
}

/**
 * A CITY WITH NOTHING TO SELL: no live data to follow (`canFollow` says
 * 'timetable') and no disruption notices to alert on. Perth today. The wall
 * there says so and takes no money.
 */
export function nothingToSell(city) {
  const facts = CITIES[city] ?? null;
  return facts?.live === false && facts?.hasAlerts === false;
}

/**
 * The noun a ride is called here. Trams and buses exist in Melbourne only, so
 * a mode's noun is honoured only where `modesOf(city)` has that mode; anywhere
 * else it is "train", or "light rail" in Canberra, whose rail is a tram line.
 */
export function rideNoun(city, wanted = '') {
  if (wanted && modesOf(city).some((m) => m.labels?.one === wanted)) return wanted;
  return city === 'canberra' ? 'light rail' : 'train';
}

/**
 * The headline and the one supporting line, per context. Short on purpose: the
 * sheet has to fit one screen. `reason` is why the wall opened; `null` is the
 * plain offer (the welcome, a first look).
 *
 *   null / refused   Never miss your stop.
 *   lapsed           Alerts are off.
 *   follow           Follow this train to your stop.   (noun: the ride's own)
 *   alerts / test    Know the moment your line breaks.
 *   legacy           Alerts are now part of the subscription.
 */
export function paywallHeadline({
  reason = null, legacy = false, noun = 'train', freeFollow = null, trial = 0,
} = {}) {
  if (legacy) {
    return {
      title: 'Alerts are now part of the subscription.',
      why: `${trial ? `Start ${span(trial)} free` : 'Subscribe'} to keep them.`,
    };
  }
  if (reason === 'lapsed') {
    return { title: 'Alerts are off.', why: 'Renew to bring them back.' };
  }
  if (reason === 'follow') {
    return {
      title: `Follow this ${noun} to your stop.`,
      why: freeFollow === 'used'
        ? 'You’ve had your free Follow.'
        : 'Following is part of the subscription.',
    };
  }
  if (reason === 'alerts' || reason === 'test') {
    return { title: 'Know the moment your line breaks.', why: 'Alerts are part of the subscription.' };
  }
  return { title: 'Never miss your stop.', why: 'The board and your lines stay free.' };
}

/**
 * What the wall says, as data. The component draws this and nothing else, so
 * every state it can be in is testable without a WebView.
 *
 * @param {object} s
 * @param {string} s.platform       'ios' | 'android'
 * @param {string} s.reason         'alerts' | 'follow' | 'test' | 'lapsed' | 'refused' | 'legacy'
 * @param {string} s.products       'loading' | 'ready' | 'error'
 * @param {Array}  s.plans          from `orderPlans`
 * @param {string} s.selected       the chosen plan's id; yearly when unset
 * @param {string} s.purchase       'idle' | 'buying' | 'pending' | 'cancelled' | 'error' | 'done'
 * @param {string} s.restore        'idle' | 'restoring' | 'none' | 'error' | 'done'
 * @param {string} s.origin         where the Privacy and Terms pages live
 * @param {boolean} s.subscribedBefore  has this phone ever been entitled? False
 *                                  is the pre-paywall tester: alerts on, never paid
 * @param {boolean} s.freeFollow    'unused' | 'used' | null (no free Follow here)
 * @param {object} s.line           { name, colour } of the first watched line
 * @param {object} s.ride           { livery, noun } of the vehicle to draw and name
 */
export function paywallView({
  platform = 'ios', reason = null, products = 'loading', plans = [], selected = null,
  purchase = 'idle', restore = 'idle', origin = '', error = null,
  subscribedBefore = true, freeFollow = null, line = null, ride = null, ctx = null,
} = {}) {
  const noSale = nothingToSell(ride?.city);
  if (noSale) { plans = []; products = 'ready'; }
  const apple = platform !== 'android';
  const storeName = apple ? 'the App Store' : 'Google Play';
  const account = apple ? 'your Apple ID' : 'your Google Play account';

  const chosen = plans.find((p) => p.id === selected) ?? plans[0] ?? null;
  const trial = chosen?.trialEligible && chosen.trialDays ? chosen.trialDays : 0;

  // NEVER SUBSCRIBED IS NOT A LAPSE. A tester who had Alerts on before the
  // paywall existed has not had a subscription end: telling them one did is
  // telling them something false about their own money.
  const legacy = reason === 'legacy'
    || ((reason === 'lapsed' || reason === 'refused') && subscribedBefore === false);

  // `ride` is where the phone is (its city's own train); `ctx` is what the
  // caller was doing (a tram ride), honoured only where that mode exists.
  const city = ride?.city ?? null;
  const ctxNoun = ctx?.noun && modesOf(city).some((m) => m.labels?.one === ctx.noun) ? ctx.noun : null;
  const noun = ctxNoun ?? rideNoun(city);
  const heroLivery = (ctxNoun ? ctx.livery : null) || ride?.livery || null;
  const headline = noSale
    ? { title: `${CITIES[city].name} has nothing live yet.`, why: 'The board and your lines stay free.' }
    : paywallHeadline({ reason, legacy, noun, freeFollow, trial });
  const { title, why } = headline;

  const busy = purchase === 'buying' || restore === 'restoring';
  let status = null;
  if (products === 'loading') status = `Asking ${storeName} for prices…`;
  else if (products === 'error') status = `${storeName[0].toUpperCase()}${storeName.slice(1)} did not answer. Check your connection and try again.`;
  else if (products === 'ready' && !plans.length && !noSale) status = 'Not available yet. Everything else in the app still works.';
  if (purchase === 'buying') status = `Waiting for ${storeName}…`;
  else if (purchase === 'pending') status = 'Waiting for approval. Alerts turn on as soon as it goes through.';
  else if (purchase === 'cancelled') status = 'No charge. Nothing changed.';
  else if (purchase === 'error') status = error || 'The purchase did not go through. Nothing was charged.';
  if (restore === 'restoring') status = 'Looking for a subscription…';
  else if (restore === 'none') status = `No subscription found on ${account}.`;
  else if (restore === 'error') status = `Could not reach ${storeName} to restore. Try again.`;

  const terms = [
    `Payment is charged to ${account}${trial ? ' when the free trial ends' : ' when you confirm'}.`,
    'The subscription renews automatically at the same price unless it is cancelled at least 24 hours before the end of the current period.',
    apple
      ? 'Manage or cancel it in your App Store account settings.'
      : 'Manage or cancel it in Google Play under Subscriptions.',
  ].join(' ');

  const saving = planSaving(plans);
  const locked = busy || purchase === 'pending';

  const timeline = chosen && trial ? [
    { day: 'TODAY', text: 'Alerts and Follow on. Nothing to pay.' },
    {
      day: `DAY ${trial}`,
      text: chosen.period === 'year'
        ? `${chosen.price} for the year, unless you cancel before then.`
        : `${chosen.price} a month, unless you cancel before then.`,
    },
  ] : null;

  const cta = chosen ? {
    planId: chosen.id,
    label: trial ? (trial === 7 ? 'Start free week' : `Start ${span(trial)} free`) : `Subscribe · ${priceShort(chosen)}`,
    sub: 'Cancel anytime in Settings.',
    disabled: locked,
  } : null;

  return {
    stamp: trial ? `LATE AGAIN · ${span(trial).toUpperCase()} FREE` : 'LATE AGAIN · SUBSCRIPTION',
    title,
    why,
    legacy,
    demo: paywallDemo({ reason, line, platform }),
    // No rail with an alarm on it where nothing is live to ring it (Perth).
    noSale,
    dismiss: noSale ? { label: 'Got it' } : null,
    hero: { livery: heroLivery, noun, rail: CITIES[city]?.live !== false },
    benefits: paywallBenefits({ platform, noun, city, freeFollow: freeFollow === 'unused' && reason !== 'follow' }),
    status,
    retry: products === 'error',
    busy,
    selected: chosen?.id ?? null,
    saving,
    plans: plans.map((p) => ({
      id: p.id,
      primary: p.period === 'year',
      selected: p.id === chosen?.id,
      label: p.period === 'year' ? 'Yearly' : 'Monthly',
      price: priceLine(p),
      short: priceShort(p),
      period: p.period,
      amount: p.price,
      // The chip on the yearly card, from the store's own two prices.
      chip: p.period === 'year' && saving ? `Best value · save ${saving.percent}%` : null,
      // What is true of THIS account on this plan: the trial only when the
      // store says it is eligible, otherwise the per-month sum.
      note: p.trialEligible && p.trialDays
        ? `${span(p.trialDays)} free`
        : (p.period === 'year' && saving ? `${saving.perMonth}/mo` : null),
      // Subordinate by construction: only yearly carries it, and it is drawn
      // smaller and dimmer than the billed price above it.
      sub: p.period === 'year' && saving ? saving.line : null,
      trial: trialLine(p),
      disabled: locked,
    })),
    timeline,
    cta,
    terms: noSale ? '' : terms,
    restoreLabel: 'Restore',
    termsLabel: 'Terms of Use',
    termsUrl: apple ? APPLE_EULA : `${origin}${TERMS_PATH}`,
    privacyLabel: 'Privacy',
    privacyUrl: `${origin}${PRIVACY_PATH}`,
  };
}

/**
 * What Settings says when Alerts were switched on here and the phone may not
 * have them. Two different facts: a subscription that ended, and a phone that
 * had Alerts on before there was a subscription to buy (`subscribedBefore`
 * false). `trialDays` is the store's offer to THIS account, or 0.
 */
export function lockedAlertsLine({ subscribedBefore = true, trialDays = 0 } = {}) {
  if (subscribedBefore) return 'Your subscription ended, so alerts are off. Nothing will reach this phone.';
  return `Alerts are now part of the subscription. ${trialDays ? `Start ${span(trialDays)} free` : 'Subscribe'} to keep them.`;
}

/** Which wall the Alerts switch opens: the plain offer, a lapse, or the legacy note. */
export function alertsWallReason({ pushEnabled = false, locked = false, subscribedBefore = true } = {}) {
  if (!pushEnabled || !locked) return 'alerts';
  return subscribedBefore ? 'lapsed' : 'legacy';
}

/** The one-line status on the Settings screen. */
export function statusLine({ available, entitled, entitlement, platform = 'ios' }) {
  if (!available) return null;
  if (!entitled) return 'Not subscribed. Alerts and Follow need a subscription.';
  const period = /monthly|month/.test(String(entitlement?.productId ?? '') + String(entitlement?.basePlanId ?? ''))
    ? 'Monthly' : /annual|year/.test(String(entitlement?.productId ?? '') + String(entitlement?.basePlanId ?? '')) ? 'Yearly' : 'Subscribed';
  const ms = Date.parse(entitlement?.expiresAt ?? '');
  const until = Number.isFinite(ms) ? `, current until ${new Date(ms).toISOString().slice(0, 10)}` : '';
  const where = platform === 'android' ? 'Google Play' : 'App Store';
  return `${period} via ${where}${until}.`;
}

// ── The state machine ────────────────────────────────────────────────────────

/**
 * The plugin as PLAIN BOUND METHODS, never the proxy. A Capacitor plugin proxy
 * answers every property, `then` included, and awaiting one hangs for ever —
 * see `liveActivity()` in src/follow-bridge.js for the whole story.
 */
export function wrapPlugin(p) {
  if (!p) return null;
  const call = (name) => (...a) => p[name](...a);
  return {
    status: call('status'),
    products: call('products'),
    purchase: call('purchase'),
    current: call('current'),
    restore: call('restore'),
    manage: call('manage'),
    acknowledge: call('acknowledge'),
    addListener: call('addListener'),
  };
}

function withTimeout(promise, ms, fallback) {
  return Promise.race([
    Promise.resolve(promise),
    new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);
}

export function createBilling({
  getPlugin = () => null,
  platform = 'ios',
  apiBase = '',
  origin = '',
  fetchImpl = typeof fetch === 'function' ? fetch : null,
  store = {},
  now = () => Date.now(),
  openUrl = () => {},
  // Told 'trial_started' or 'subscribed' once a purchase made on this phone is
  // confirmed. Fire and forget; see src/attribution.js.
  onPurchased = () => {},
} = {}) {
  const available = ref(false);
  const entitlement = ref(readStored());
  const proof = ref(null);
  const productState = ref('idle');
  const plans = ref([]);
  const purchaseState = ref('idle');
  const restoreState = ref('idle');
  const purchaseError = ref(null);
  const wall = ref({ open: false, reason: null });
  const lapsePending = ref(store[LAPSE_KEY] === true);
  const selected = ref(null);
  const freeFollow = ref(readFreeFollow());
  // Where the demo's line comes from. Set by src/app-state.js, which imports
  // this file and so cannot be imported by it.
  const lineSource = shallowRef(() => null);
  const rideSource = shallowRef(() => null);

  /**
   * HAS THIS PHONE EVER BEEN ENTITLED? Not the same as "is it now", and the
   * difference is the pre-paywall tester: Alerts on since before there was
   * anything to buy, never subscribed. Telling them their subscription ended
   * is false. A stored entitlement from a store, or a lapse still waiting to
   * be announced, counts for the phones that subscribed before this flag was
   * written.
   */
  const everEntitled = ref(
    store[EVER_KEY] === true
      || lapsePending.value
      || entitlement.value?.entitled === true
      || entitlement.value?.store === 'apple'
      || entitlement.value?.store === 'google',
  );

  /**
   * TRUE ONCE THE FIRST ANSWER IS IN, or once `READY_WAIT_MS` has passed
   * without one. Until then whether this phone may alert is not known, and a
   * switch that shows ON for three seconds and then flips OFF has told
   * somebody something false twice. A stored yes from the last launch is
   * known enough to show at once: the answer that would contradict it is a
   * lapse, and a lapse is announced loudly anyway.
   */
  const settled = ref(false);
  const known = computed(() => settled.value || entitlement.value?.entitled === true);

  const entitled = computed(() => entitlement.value?.entitled === true && !expired(entitlement.value));

  let api = null;
  let waiters = [];
  let readyResolve;
  const ready = new Promise((resolve) => { readyResolve = resolve; });

  function readStored() {
    const raw = store[ENTITLEMENT_KEY];
    return raw && typeof raw === 'object' ? raw : null;
  }

  function readFreeFollow() {
    const raw = store[FREE_FOLLOW_KEY];
    return raw && typeof raw === 'object' && raw.usedAt ? raw : null;
  }

  function expired(e) {
    // Only a SERVER answer carries an expiry worth enforcing locally; a local
    // one is StoreKit's or Play's word that it is current right now. A grace
    // hour so a renewal that lands a minute late is not a lapse.
    if (!e?.expiresAt || e.source !== 'server') return false;
    const ms = Date.parse(e.expiresAt);
    return Number.isFinite(ms) && ms + 60 * 60_000 < now();
  }

  function save(next) {
    // The RAW flag, not `entitled`: a stored yes whose expiry has already
    // passed reads as no before anything is saved, and comparing no with no
    // would swallow exactly the lapse that happened while the app was closed.
    const was = entitlement.value?.entitled === true;
    entitlement.value = next;
    store[ENTITLEMENT_KEY] = next;
    const is = entitled.value;
    // A LAPSE IS LOUD. Yes → no is remembered, and the next open says so once.
    if (was && !is) {
      lapsePending.value = true;
      store[LAPSE_KEY] = true;
    }
    if (is && lapsePending.value) {
      lapsePending.value = false;
      store[LAPSE_KEY] = false;
    }
    if (is && !everEntitled.value) {
      everEntitled.value = true;
      store[EVER_KEY] = true;
    }
  }

  /** POST /api/entitlement. `{ ok, status, data }`; never throws. */
  async function ask(purchase) {
    if (!fetchImpl) return { ok: false };
    try {
      const res = await fetchImpl(`${apiBase}/api/entitlement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ purchase }),
      });
      const data = await res.json?.().catch(() => null);
      return { ok: !!res.ok, status: res.status, data };
    } catch {
      return { ok: false };
    }
  }

  /**
   * Decide, from a proof (or none). The server's answer when it gives one; the
   * store's local word otherwise.
   */
  async function settle(raw, { localExpiresAt = null } = {}) {
    const p = validProof(raw);
    proof.value = p;
    if (!p) {
      save({ entitled: false, store: null, productId: null, expiresAt: null, source: 'local', checkedAt: new Date(now()).toISOString() });
      return false;
    }
    const res = await ask(p);
    // Any answer that SAYS whether, below a 5xx, is the server's word — a 400
    // for a forged JWS included. A 5xx, a 404 from a server that predates the
    // route, or no answer at all is "could not ask".
    // `verification-unavailable` is the server failing to reach Apple or
    // Google (it answers 503, or names it as a reason): unknown, not a no.
    const said = res.status && res.status < 500 && res.data && typeof res.data === 'object'
      && 'entitled' in res.data && res.data.reason !== 'verification-unavailable';
    const body = said ? res.data : null;
    if (body) {
      save({
        entitled: body.entitled === true,
        store: body.store ?? p.store,
        productId: body.productId ?? p.productId ?? null,
        basePlanId: p.basePlanId ?? null,
        expiresAt: body.expiresAt ?? null,
        environment: body.environment ?? null,
        source: 'server',
        checkedAt: new Date(now()).toISOString(),
      });
    } else {
      // Unreachable, or a server that predates the route. The store's own
      // verified state stands in.
      save({
        entitled: true,
        store: p.store,
        productId: p.productId ?? raw?.productId ?? null,
        basePlanId: p.basePlanId ?? null,
        expiresAt: localExpiresAt ?? raw?.expiresAt ?? null,
        source: 'local',
        checkedAt: new Date(now()).toISOString(),
      });
    }
    // PLAY REFUNDS AN UNACKNOWLEDGED PURCHASE AFTER THREE DAYS. The server
    // acknowledges when it verifies; if it could not be reached, or says it
    // did not, the phone does it itself.
    if (p.store === 'google' && raw?.acknowledged !== true && (!body || body.acknowledged === false)) {
      try { await api?.acknowledge({ purchaseToken: p.purchaseToken }); } catch { /* next launch retries */ }
    }
    return entitled.value;
  }

  /** Ask the store what this phone holds, then settle it. */
  async function refresh() {
    if (!available.value || !api) return entitled.value;
    let cur;
    try {
      cur = await api.current();
    } catch {
      // The store did not answer. Keep what we had rather than inventing a
      // lapse out of a bad connection.
      return entitled.value;
    }
    const raw = cur?.purchase ?? null;
    return settle(raw, { localExpiresAt: cur?.expiresAt ?? raw?.expiresAt ?? null });
  }

  async function probe() {
    const p = getPlugin();
    if (!p) return false;
    try {
      const answer = await withTimeout(p.status(), 3000, null);
      return answer?.available === true;
    } catch {
      // "not implemented": a build whose native half predates the plugin.
      return false;
    }
  }

  /** Once per launch. Resolves `ready` whatever happens. */
  let started = null;
  function start() {
    if (started) return started;
    started = (async () => {
      const raw = getPlugin();
      if (!raw || !(await probe())) {
        available.value = false;
        return;
      }
      api = raw;
      available.value = true;
      try {
        api.addListener('billingUpdate', (event) => { onUpdate(event); });
      } catch { /* updates arrive on the next refresh instead */ }
      await refresh();
    })()
      .catch(() => {})
      .finally(() => {
        settled.value = true;
        readyResolve();
      });
    // The same bound every gate waits: past it, decide on what is known (the
    // plugin's presence, and the entitlement stored last launch).
    const timer = setTimeout(() => { settled.value = true; }, READY_WAIT_MS);
    timer?.unref?.();
    return started;
  }

  async function onUpdate(event) {
    if (event?.state === 'pending') { purchaseState.value = 'pending'; return; }
    const raw = event?.purchase ?? null;
    if (raw) await settle(raw, { localExpiresAt: event?.expiresAt ?? null });
    else await refresh();
    if (entitled.value) closeWall(true);
  }

  // ── The wall ──────────────────────────────────────────────────────────────

  async function loadProducts() {
    if (!api) { productState.value = 'error'; return; }
    productState.value = 'loading';
    try {
      const answer = await api.products();
      plans.value = orderPlans(answer?.products ?? []);
      productState.value = 'ready';
    } catch {
      plans.value = [];
      productState.value = 'error';
    }
  }

  /**
   * Ask for prices ahead of the wall, once, where there is something to sell.
   * For the screens that say "7 days free" on a button before anybody has
   * pressed it (the welcome, the Settings note): the store decides whether this
   * account can have a trial, and naming one it cannot is the one wrong thing
   * to say. Silent either way; the wall asks again if this failed.
   */
  async function prefetch() {
    await whenReady();
    if (!available.value || entitled.value) return;
    if (productState.value === 'idle' || productState.value === 'error') await loadProducts();
  }

  /**
   * The free trial the store offers THIS account on the plan on offer, in
   * days, or 0 — including 0 for "the store has not said yet".
   */
  const trialOffered = computed(() => {
    const plan = plans.value.find((p) => p.id === selected.value) ?? plans.value[0];
    return plan?.trialEligible && plan.trialDays ? plan.trialDays : 0;
  });

  /** Choose a plan on the wall. Choosing is not buying. */
  function select(planId) {
    if (plans.value.some((p) => p.id === planId)) selected.value = planId;
  }

  function openWall(reason = 'alerts', ride = null) {
    purchaseState.value = 'idle';
    restoreState.value = 'idle';
    purchaseError.value = null;
    // Yearly, every time the wall opens: it is the offer (PAYWALL.md).
    selected.value = null;
    wall.value = ride ? { open: true, reason, ride } : { open: true, reason };
    if (productState.value !== 'ready' || !plans.value.length) loadProducts();
  }

  function closeWall(result = false) {
    if (wall.value.open) wall.value = { ...wall.value, open: false };
    const pending = waiters;
    waiters = [];
    for (const resolve of pending) resolve(result === true || entitled.value);
  }

  /**
   * THE GATE. Resolves true when the caller may go on: no plugin (nothing to
   * sell, behave as always), or entitled. Otherwise opens the wall and
   * resolves when it closes — true if they bought or restored on it, so the
   * caller can carry on and turn alerts on without a second tap.
   */
  async function requireEntitlement(reason = 'alerts', ride = null) {
    start();
    await withTimeout(ready, READY_WAIT_MS, null);
    if (!available.value) return true;
    if (entitled.value) return true;
    // ONE FREE FOLLOW. The whole of the lock-screen experience, once, before
    // the wall: Follow is the feature nobody believes until they have felt the
    // buzz before their stop. It is claimed when the follow actually starts
    // (`claimFreeFollow`), so a follow the system refused does not spend it.
    if (reason === 'follow' && !freeFollow.value) return true;
    return new Promise((resolve) => {
      waiters.push(resolve);
      openWall(reason, ride);
    });
  }

  /**
   * The first answer, if one has been asked for; bounded. Nothing to wait for
   * where nothing was started (a test, the web).
   */
  function whenReady(ms = READY_WAIT_MS) {
    return started ? withTimeout(ready, ms, null) : Promise.resolve();
  }

  /** May this phone be interrupted? Synchronous; for the local-alert gate. */
  function mayAlert() {
    return !available.value || entitled.value;
  }

  /**
   * May this phone start (or keep) this follow? Everything `mayAlert` allows,
   * plus the one free Follow: unused, or this is the follow it was spent on.
   */
  function mayFollow(key = null) {
    if (mayAlert()) return true;
    const used = freeFollow.value;
    return !used || (key != null && used.key === key);
  }

  /**
   * A follow started without a subscription: that is the free one, spent.
   * Remembered on the phone, and the server keeps its own count (see
   * server/free-follow.mjs), so clearing the app's storage is not a refill.
   */
  function claimFreeFollow(key) {
    if (mayAlert() || freeFollow.value) return false;
    const used = { usedAt: new Date(now()).toISOString(), key: key == null ? null : String(key) };
    freeFollow.value = used;
    store[FREE_FOLLOW_KEY] = used;
    return true;
  }

  /** 'unused' | 'used', or null where nothing is sold or it is all paid for. */
  const freeFollowState = computed(() => {
    if (!available.value || entitled.value) return null;
    return freeFollow.value ? 'used' : 'unused';
  });

  async function buy(planId = null) {
    const id = planId ?? selected.value ?? plans.value[0]?.id;
    const plan = plans.value.find((p) => p.id === id);
    if (!api || !plan || purchaseState.value === 'buying') return false;
    purchaseState.value = 'buying';
    purchaseError.value = null;
    try {
      const args = platform === 'android'
        ? { productId: GOOGLE_PRODUCT, basePlanId: plan.basePlanId ?? plan.id }
        : { productId: plan.productId ?? plan.id };
      const result = await api.purchase(args);
      const state = result?.state ?? (result?.purchase ? 'purchased' : 'cancelled');
      if (state === 'cancelled') { purchaseState.value = 'cancelled'; return false; }
      if (state === 'pending') { purchaseState.value = 'pending'; return false; }
      if (state !== 'purchased') throw new Error(result?.message || 'unknown');
      await settle(result.purchase, { localExpiresAt: result?.expiresAt ?? null });
      if (!entitled.value) {
        purchaseState.value = 'error';
        purchaseError.value = 'The store took the purchase but it could not be confirmed. Try Restore purchases.';
        return false;
      }
      purchaseState.value = 'done';
      // A BUY, not a restore or a renewal: those are not a conversion. A free
      // trial is the plan the store offered a trial on, taken.
      try { onPurchased(plan.trialEligible && plan.trialDays ? 'trial_started' : 'subscribed'); } catch { /* never the purchase's problem */ }
      closeWall(true);
      return true;
    } catch (e) {
      purchaseState.value = 'error';
      purchaseError.value = null;
      if (/cancel/i.test(String(e?.message))) purchaseState.value = 'cancelled';
      return false;
    }
  }

  async function restore() {
    if (!api) return false;
    restoreState.value = 'restoring';
    try {
      const cur = await api.restore();
      const raw = cur?.purchase ?? null;
      await settle(raw, { localExpiresAt: cur?.expiresAt ?? null });
      restoreState.value = entitled.value ? 'done' : 'none';
      if (entitled.value) closeWall(true);
      return entitled.value;
    } catch {
      restoreState.value = 'error';
      return false;
    }
  }

  async function manage() {
    if (!available.value) return;
    try {
      await api.manage();
    } catch {
      openUrl(platform === 'android' ? PLAY_MANAGE : 'https://apps.apple.com/account/subscriptions');
    }
  }

  /**
   * A server said 402 not-entitled to a subscribe or a follow register. Its
   * word beats ours: record the lapse, and offer the wall where there is one.
   */
  function refused(reason = 'refused') {
    if (!available.value) return;
    if (entitled.value) {
      save({ ...(entitlement.value ?? {}), entitled: false, source: 'server', checkedAt: new Date(now()).toISOString() });
    }
    // A phone that has never been entitled and is refused a subscribe is a
    // phone that had Alerts on before the paywall: not a lapse. See
    // `everEntitled`.
    const why = reason === 'refused' && !everEntitled.value ? 'legacy' : reason;
    // A follow refused by the server has had its free one, whatever this
    // phone remembers (a reinstall, cleared storage): the server keeps count.
    if (reason === 'follow') claimFreeFollow(null);
    if (wall.value.open) return;
    // A follow refusal answers a tap, so it always shows. A subscribe refusal
    // is a background sync (every change, every resume): offered on its own
    // once a week at most, or the wall pops up all day for somebody who has
    // already said no. The Alerts switch still opens it whenever it is tapped.
    if (reason !== 'follow') {
      const last = Number(store[REFUSED_WALL_KEY]) || 0;
      if (now() - last < REFUSED_WALL_EVERY_MS) return;
      store[REFUSED_WALL_KEY] = now();
    }
    openWall(why);
  }

  /** The lapse notice: once, on the next open after entitled went yes → no. */
  function showLapseIfDue() {
    if (!available.value || entitled.value || !lapsePending.value) return false;
    lapsePending.value = false;
    store[LAPSE_KEY] = false;
    openWall('lapsed');
    return true;
  }

  /** Resume: re-check, then say so if it lapsed while we were away. */
  async function resume() {
    await start();
    await refresh();
    showLapseIfDue();
  }

  const view = computed(() => paywallView({
    platform,
    reason: wall.value.reason,
    products: productState.value === 'idle' ? 'loading' : productState.value,
    plans: plans.value,
    purchase: purchaseState.value,
    restore: restoreState.value,
    origin,
    error: purchaseError.value,
    selected: selected.value,
    subscribedBefore: everEntitled.value,
    freeFollow: freeFollowState.value,
    line: lineSource.value?.() ?? null,
    ride: rideSource.value?.() ?? null,
    ctx: wall.value.ride ?? null,
  }));

  /** Where the demo gets the first watched line: `() => ({ name, colour })`. */
  /** Where the hero's vehicle comes from: `() => ({ livery, noun })`. */
  function setRideSource(fn) {
    rideSource.value = typeof fn === 'function' ? fn : () => null;
  }

  function setLineSource(fn) {
    lineSource.value = typeof fn === 'function' ? fn : () => null;
  }

  const status = computed(() => statusLine({
    available: available.value, entitled: entitled.value, entitlement: entitlement.value, platform,
  }));

  return {
    available, entitled, entitlement, proof, plans, productState, purchaseState, restoreState,
    wall, view, status, lapsePending, ready, known, everEntitled, selected, trialOffered,
    freeFollow, freeFollowState,
    start, whenReady, refresh, resume, requireEntitlement, mayAlert, mayFollow, claimFreeFollow,
    buy, restore, manage, select, prefetch, setLineSource, setRideSource,
    openWall, closeWall, loadProducts, refused, showLapseIfDue,
    currentProof: () => proof.value,
  };
}

// ── The app's instance ───────────────────────────────────────────────────────

function openExternalUrl(url) {
  try {
    const Browser = nativePlugin('Browser');
    if (isNative && Browser?.open) { Browser.open({ url }); return; }
  } catch { /* fall through */ }
  try { window.open(url, '_blank', 'noopener'); } catch { /* nothing to open with */ }
}

const PUBLIC = (() => {
  try { return isNative ? API_BASE : (typeof location !== 'undefined' ? location.origin : ''); } catch { return ''; }
})();

export const billing = createBilling({
  // No billing in the demo, and none on the web: nothing to sell there, so
  // nothing is gated there.
  getPlugin: () => (isNative && !isDemo.value ? wrapPlugin(nativePlugin('Billing')) : null),
  platform: nativePlatform,
  apiBase: API_BASE,
  origin: PUBLIC,
  store: prefs,
  openUrl: openExternalUrl,
  onPurchased: (name) => { attribution.event(name).catch(() => {}); },
});

export const {
  available: billingAvailable, entitled, entitlement, wall, view: paywall, status: subscriptionStatus,
  requireEntitlement, mayAlert, mayFollow, claimFreeFollow, currentProof, refused: entitlementRefused,
  known: billingKnown, everEntitled, trialOffered,
} = billing;

export { openExternalUrl };
